import { Link } from 'react-router-dom';
import { Bean, CircleDot, Disc, Gamepad2, Ship, Target } from 'lucide-react';
import {
  gamePostPath,
  isGuestId,
  resolveGamePlayer,
  type GameKind,
  type GameMeta,
} from '@awwyeah/shared';
import { formatClock } from '../feed';
import { gameInstant } from '../feedItems';
import { gameOutcomeLabel, kindLabel } from '../gameFact';
import type { MemberDirectory } from '../members';

/**
 * A finished game, in the feed (ROADMAP "Games in the family timeline").
 *
 * A quiet fact card, deliberately smaller than `CheckinPost` — no photo, no seen-by (see
 * `../feedItems.ts` for why), nothing tappable but the whole row. Player names ride in
 * their own member colors, same as everywhere else in the app that names a person; the
 * outcome reads as a fact ("Faye won" / "Draw"), never a record. Tapping opens the
 * replayable board (`gamePostPath`) — the actual game, not a summary of it.
 */
export function GameFeedCard({
  game,
  members,
}: {
  game: GameMeta;
  members: MemberDirectory;
}): JSX.Element {
  const [playerAId, playerBId] = game.playerIds;
  // A seat may be a guest (WS-23), who is by definition not in the member directory — that
  // already resolved safely (to "Someone" with a hashed colour), but a guest who told us
  // their name deserves it, in the neutral guest slate rather than borrowing a member's.
  const named = (id: string): { name: string; color: string } =>
    isGuestId(id) ? resolveGamePlayer(id, [], game.guestNames) : members.get(id);
  const playerA = named(playerAId);
  const playerB = named(playerBId);
  const winner = game.winnerId ? named(game.winnerId) : null;
  const instant = gameInstant(game);

  return (
    <Link
      to={gamePostPath(game.id)}
      data-testid="checkin-game-card"
      data-game-id={game.id}
      className="flex items-center gap-2.5 border-y border-line bg-surface px-4 py-3"
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-raised text-fg-2">
        <KindGlyph kind={game.kind} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] leading-[1.35] text-fg">
          <span style={{ color: playerA.color }} className="font-semibold">
            {playerA.name}
          </span>
          <span className="text-fg-3"> &amp; </span>
          <span style={{ color: playerB.color }} className="font-semibold">
            {playerB.name}
          </span>
          <span className="text-fg-2"> played {kindLabel(game.kind)}</span>
        </div>
        <div className="truncate text-xs text-fg-3">
          {gameOutcomeLabel(game, winner?.name ?? null)} · {formatClock(instant)}
        </div>
      </div>
    </Link>
  );
}

/** One glyph per kind, generic off `GameMeta.kind`, with a neutral fallback for a kind
 * this build doesn't recognize yet — same shape as `apps/web/src/modules/games/
 * GamesSection.tsx`'s `KindIcon`, duplicated for the same "no cross-module reach" reason
 * as `../gameFact.ts`. */
function KindGlyph({ kind }: { kind: GameKind | string }): JSX.Element {
  const Icon =
    kind === 'connect4'
      ? CircleDot
      : kind === 'battleship'
        ? Ship
        : kind === 'checkers'
          ? Disc
          : kind === 'pool'
            ? Target
            : kind === 'mancala'
              ? Bean
              : Gamepad2;
  return <Icon size={16} strokeWidth={1.75} aria-hidden="true" data-testid="game-kind-icon" />;
}
